import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XsToXlSize } from '@topcon/tuicl';
import { TeIcon, TeImage } from '@topcon/tuicl-react';
import { PlaceholderContainer, Section } from '@topcon/topcon-utils';

import IconWrapper from '../IconWrapper';

// Need to scale up to better fit default image size
const PlaceholderIcon = styled(TeIcon)<{ scale: number }>`
  transform: ${(props) => `scale(${props.scale})`};
`;

function Loader({ size }: { size: number }) {
  const { t } = useTranslation('settings');
  return (
    <Section $padding={`${size / 8}px`}>
      <PlaceholderContainer
        borderRadius={4}
        grow
        backgroundColor="background.secondary"
        hasLoader
        text={t('loading')}
      />
    </Section>
  );
}

type CompanyIconProps = {
  size?: XsToXlSize | 'xxl';
  iconURL?: string;
  loading?: boolean;
};

function CompanyIcon({ size = 'l', iconURL, loading }: CompanyIconProps) {
  const sizeMap = new Map<XsToXlSize | 'xxl', number>([
    ['xxl', 128],
    ['xl', 64],
    ['l', 48],
    ['m', 32],
    ['s', 24],
    ['xs', 16],
  ]);
  const sizeInPixels = sizeMap.get(size);
  const [companyImageUrl, setCompanyImageUrl] = useState<string>();
  const [error, setError] = useState(false);

  const renderPlaceholder = () => {
    const isXXL = size === 'xxl';
    const defaultSize = size !== 'xxl' ? size : 'xl';
    // The base TeIcon glyph renders at 50% of the CompanyIcon box; scale it up so
    // the placeholder sits at ~60% of the circle with padding, matching the design.
    const scale = isXXL ? 2.4 : 1.2;

    return <PlaceholderIcon name="business" size={defaultSize} scale={scale} />;
  };

  useEffect(() => {
    let cancelled = false;

    // Preload via native Image so we can detect failures before handing off to TeImage
    // it could happen that the image fetch fails, so this is a hack to be able to detect that
    const img = new Image();
    img.onload = () => {
      if (!cancelled) {
        setCompanyImageUrl(iconURL);
        setError(false);
      }
    };
    img.onerror = () => {
      if (!cancelled) {
        setError(true);
        setCompanyImageUrl('');
      }
    };
    img.src = iconURL || '';

    return () => {
      cancelled = true;
    };
  }, [iconURL]);

  return (
    <IconWrapper size={sizeInPixels}>
      {/* LOADING */}
      {loading && <Loader size={sizeInPixels!} />}
      {/* PLACEHOLDER FOR ERROR */}
      {!loading && (error || !companyImageUrl) && renderPlaceholder()}
      {/* IMAGE */}
      {!loading && !error && companyImageUrl && (
        <TeImage
          imageSource={companyImageUrl}
          height={sizeInPixels}
          width={sizeInPixels}
          backgroundPosition="center"
          backgroundSize="contain"
        />
      )}
    </IconWrapper>
  );
}

export default CompanyIcon;
